/**
 * DataTable Component
 * Based on shadcn/ui + Figma design specification
 * @description shadcn/ui 기반 컴포넌트. CSS 변수를 활용한 테마 시스템 적용.
 */

import { forwardRef, useState, ReactNode, HTMLAttributes, TdHTMLAttributes, ThHTMLAttributes } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../lib/utils';
import { ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react';
import { Checkbox } from './checkbox';
import { Skeleton } from './skeleton';
import { Badge } from './badge';
import { Avatar, AvatarImage, AvatarFallback } from './avatar';

// ============ Types ============
export type TableHeaderVariant = 'default' | 'hover' | 'active' | 'selected';
export type TableRowVariant = 'default' | 'hover' | 'active' | 'selected';
export type TableCellVariant = 'default' | 'hover' | 'active' | 'selected';
export type TableCellAlign = 'left' | 'center' | 'right';

export interface DataTableProps extends HTMLAttributes<HTMLTableElement> {
  children: ReactNode;
  /** true면 스크롤용 div 없이 table만 렌더 (상위에서 이미 overflow 제어할 때 헤더/본문 열 정렬 유지용) */
  noScrollWrapper?: boolean;
}

export interface DataTableHeaderProps extends HTMLAttributes<HTMLTableSectionElement> {
  children: ReactNode;
}

export interface DataTableHeadProps extends ThHTMLAttributes<HTMLTableCellElement> {
  variant?: TableHeaderVariant;
  sortable?: boolean;
  sorted?: 'asc' | 'desc' | false;
  onSort?: () => void;
}

export interface DataTableBodyProps extends HTMLAttributes<HTMLTableSectionElement> {
  children: ReactNode;
}

export interface DataTableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  variant?: TableRowVariant;
  selected?: boolean;
}

export interface DataTableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  variant?: TableCellVariant;
  align?: TableCellAlign;
}

export interface DataTableCellTextProps extends DataTableCellProps {
  subtitle?: string;
}

export interface DataTableCellCheckboxProps extends DataTableCellProps {
  checked?: boolean;
  indeterminate?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}

export interface DataTableCellBadgeProps extends DataTableCellProps {
  badgeVariant?: 'primary' | 'secondary' | 'outline' | 'destructive' | 'success' | 'warning' | 'error';
}

export interface DataTableCellAvatarProps extends DataTableCellProps {
  src?: string;
  fallback?: string;
  name?: string;
}

export interface DataTableCellActionsProps extends DataTableCellProps {
  children: ReactNode;
}

export interface ColumnDef<T> {
  id: string;
  header: string | ReactNode | ((props: { column: ColumnDef<T> }) => ReactNode);
  cell: (props: { row: T; rowIndex: number }) => ReactNode;
  accessorKey?: keyof T;
  sortable?: boolean;
  align?: TableCellAlign;
  width?: string | number;
}

export interface DataTableRootProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  loading?: boolean;
  emptyMessage?: string;
  selectable?: boolean;
  selectedRows?: T[];
  onSelectionChange?: (rows: T[]) => void;
  onRowClick?: (row: T, index: number) => void;
  getRowId?: (row: T) => string;
  className?: string;
}

// ============ Table Root ============
export const DataTable = forwardRef<HTMLTableElement, DataTableProps>(
  ({ className, children, noScrollWrapper, ...props }, ref) => {
    const table = (
      <table
        ref={ref}
        className={cn('w-full caption-bottom text-sm', className)}
        {...props}
      >
        {children}
      </table>
    );
    if (noScrollWrapper) return table;
    return <div className="relative w-full overflow-auto">{table}</div>;
  }
);
DataTable.displayName = 'DataTable';

// ============ Table Header ============
export const DataTableHeader = forwardRef<HTMLTableSectionElement, DataTableHeaderProps>(
  ({ className, children, ...props }, ref) => (
    <thead
      ref={ref}
      className={cn('data-table-header bg-muted/50 [&_tr]:border-b [&_tr]:border-border', className)}
      {...props}
    >
      {children}
    </thead>
  )
);
DataTableHeader.displayName = 'DataTableHeader';

// ============ Table Head Cell ============
const tableHeadVariants = cva(
  'data-table-head h-12 px-4 text-left align-middle font-semibold text-foreground/90 [&:has([role=checkbox])]:pr-0 border-b border-border bg-muted/40 first:rounded-tl-lg last:rounded-tr-lg',
  {
    variants: {
      variant: {
        default: '',
        hover: 'bg-accent-1',
        active: 'bg-accent-2',
        selected: 'bg-primary/10',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export const DataTableHead = forwardRef<HTMLTableCellElement, DataTableHeadProps>(
  ({ className, variant, sortable, sorted, onSort, children, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        tableHeadVariants({ variant }),
        sortable && 'cursor-pointer select-none hover:bg-accent-1',
        className
      )}
      onClick={sortable ? onSort : undefined}
      {...props}
    >
      <div className="flex items-center gap-2">
        {children}
        {sortable && (
          <span className="text-muted-foreground">
            {sorted === 'asc' ? (
              <ArrowUp className="h-4 w-4" />
            ) : sorted === 'desc' ? (
              <ArrowDown className="h-4 w-4" />
            ) : (
              <ChevronsUpDown className="h-4 w-4" />
            )}
          </span>
        )}
      </div>
    </th>
  )
);
DataTableHead.displayName = 'DataTableHead';

// ============ Table Body ============
export const DataTableBody = forwardRef<HTMLTableSectionElement, DataTableBodyProps>(
  ({ className, children, ...props }, ref) => (
    <tbody
      ref={ref}
      className={cn('[&_tr:last-child]:border-0', className)}
      {...props}
    >
      {children}
    </tbody>
  )
);
DataTableBody.displayName = 'DataTableBody';

// ============ Table Row ============
const tableRowVariants = cva(
  'border-b border-accent-2 transition-colors',
  {
    variants: {
      variant: {
        default: 'bg-card hover:bg-accent-0',
        hover: 'bg-accent-0',
        active: 'bg-accent-1',
        selected: 'bg-primary/10',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export const DataTableRow = forwardRef<HTMLTableRowElement, DataTableRowProps>(
  ({ className, variant, selected, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        tableRowVariants({ variant: selected ? 'selected' : variant }),
        className
      )}
      data-state={selected ? 'selected' : undefined}
      {...props}
    />
  )
);
DataTableRow.displayName = 'DataTableRow';

// ============ Table Cell ============
const tableCellVariants = cva(
  'p-4 align-middle [&:has([role=checkbox])]:pr-0',
  {
    variants: {
      variant: {
        default: '',
        hover: 'bg-accent-0',
        active: 'bg-accent-1',
        selected: 'bg-primary/10',
      },
      align: {
        left: 'text-left',
        center: 'text-center',
        right: 'text-right',
      },
    },
    defaultVariants: {
      variant: 'default',
      align: 'left',
    },
  }
);

export const DataTableCell = forwardRef<HTMLTableCellElement, DataTableCellProps>(
  ({ className, variant, align, ...props }, ref) => (
    <td
      ref={ref}
      className={cn(tableCellVariants({ variant, align }), className)}
      {...props}
    />
  )
);
DataTableCell.displayName = 'DataTableCell';

// ============ Specialized Cells ============

// Text Cell (1 or 2 lines)
export const DataTableCellText = forwardRef<HTMLTableCellElement, DataTableCellTextProps>(
  ({ className, variant, align, subtitle, children, ...props }, ref) => (
    <td
      ref={ref}
      className={cn(tableCellVariants({ variant, align }), className)}
      {...props}
    >
      <div>
        <div className="text-sm text-foreground">{children}</div>
        {subtitle && (
          <div className="text-xs text-muted-foreground">{subtitle}</div>
        )}
      </div>
    </td>
  )
);
DataTableCellText.displayName = 'DataTableCellText';

// Checkbox Cell
export const DataTableCellCheckbox = forwardRef<HTMLTableCellElement, DataTableCellCheckboxProps>(
  ({ className, variant, align, checked, indeterminate, onCheckedChange, ...props }, ref) => (
    <td
      ref={ref}
      className={cn(tableCellVariants({ variant, align }), 'w-12', className)}
      {...props}
    >
      <Checkbox
        checked={checked}
        indeterminate={indeterminate}
        onCheckedChange={() => onCheckedChange?.(!checked)}
      />
    </td>
  )
);
DataTableCellCheckbox.displayName = 'DataTableCellCheckbox';

// Badge Cell
export const DataTableCellBadge = forwardRef<HTMLTableCellElement, DataTableCellBadgeProps>(
  ({ className, variant, align, badgeVariant = 'primary', children, ...props }, ref) => (
    <td
      ref={ref}
      className={cn(tableCellVariants({ variant, align }), className)}
      {...props}
    >
      <Badge variant={badgeVariant === 'error' ? 'danger' : badgeVariant}>{children}</Badge>
    </td>
  )
);
DataTableCellBadge.displayName = 'DataTableCellBadge';

// Avatar Cell
export const DataTableCellAvatar = forwardRef<HTMLTableCellElement, DataTableCellAvatarProps>(
  ({ className, variant, align, src, fallback, name, ...props }, ref) => (
    <td
      ref={ref}
      className={cn(tableCellVariants({ variant, align }), className)}
      {...props}
    >
      <div className="flex items-center gap-3">
        <Avatar>
          <AvatarImage src={src} />
          <AvatarFallback>{fallback ?? name?.slice(0, 2) ?? '?'}</AvatarFallback>
        </Avatar>
        {name && <span className="text-sm text-foreground">{name}</span>}
      </div>
    </td>
  )
);
DataTableCellAvatar.displayName = 'DataTableCellAvatar';

// Actions Cell
export const DataTableCellActions = forwardRef<HTMLTableCellElement, DataTableCellActionsProps>(
  ({ className, variant, children, ...props }, ref) => (
    <td
      ref={ref}
      className={cn(tableCellVariants({ variant, align: 'right' }), className)}
      {...props}
    >
      <div className="flex items-center justify-end gap-2">
        {children}
      </div>
    </td>
  )
);
DataTableCellActions.displayName = 'DataTableCellActions';

// Skeleton Cell
export const DataTableCellSkeleton = forwardRef<HTMLTableCellElement, DataTableCellProps>(
  ({ className, ...props }, ref) => (
    <td ref={ref} className={cn('p-4', className)} {...props}>
      <Skeleton className="h-4 w-full" />
    </td>
  )
);
DataTableCellSkeleton.displayName = 'DataTableCellSkeleton';

// ============ Programmatic DataTable (convenience wrapper) ============
export function DataTableRoot<T extends object>({
  data,
  columns,
  loading,
  emptyMessage = 'No data available',
  selectable,
  selectedRows = [],
  onSelectionChange,
  onRowClick,
  getRowId,
  className,
}: DataTableRootProps<T>) {
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);

  const handleSort = (columnId: string) => {
    if (sortColumn !== columnId) {
      setSortColumn(columnId);
      setSortDirection('asc');
    } else if (sortDirection === 'asc') {
      setSortDirection('desc');
    } else {
      setSortColumn(null);
      setSortDirection(null);
    }
  };

  const sortedData = [...data].sort((a, b) => {
    if (!sortColumn || !sortDirection) return 0;
    const column = columns.find((c) => c.id === sortColumn);
    if (!column?.accessorKey) return 0;

    const aVal = a[column.accessorKey];
    const bVal = b[column.accessorKey];

    if (aVal === bVal) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;

    const comparison = aVal < bVal ? -1 : 1;
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const isAllSelected = data.length > 0 && selectedRows.length === data.length;
  const isSomeSelected = selectedRows.length > 0 && selectedRows.length < data.length;

  const handleSelectAll = () => {
    if (isAllSelected) {
      onSelectionChange?.([]);
    } else {
      onSelectionChange?.([...data]);
    }
  };

  const handleSelectRow = (row: T) => {
    const isSelected = selectedRows.includes(row);
    if (isSelected) {
      onSelectionChange?.(selectedRows.filter((r) => r !== row));
    } else {
      onSelectionChange?.([...selectedRows, row]);
    }
  };

  const getId = (row: T, index: number) => getRowId?.(row) ?? String(index);

  return (
    <DataTable className={className}>
      <DataTableHeader>
        <DataTableRow variant="default">
          {selectable && (
            <DataTableHead className="w-12">
              <Checkbox
                checked={isAllSelected}
                indeterminate={isSomeSelected}
                onCheckedChange={handleSelectAll}
              />
            </DataTableHead>
          )}
          {columns.map((column) => (
            <DataTableHead
              key={column.id}
              sortable={column.sortable}
              sorted={sortColumn === column.id ? (sortDirection as 'asc' | 'desc') : false}
              onSort={() => handleSort(column.id)}
              style={{ width: column.width }}
            >
              {typeof column.header === 'function'
                ? column.header({ column })
                : column.header}
            </DataTableHead>
          ))}
        </DataTableRow>
      </DataTableHeader>
      <DataTableBody>
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <DataTableRow key={i}>
              {selectable && <DataTableCellSkeleton />}
              {columns.map((column) => (
                <DataTableCellSkeleton key={column.id} />
              ))}
            </DataTableRow>
          ))
        ) : sortedData.length === 0 ? (
          <DataTableRow>
            <DataTableCell
              colSpan={columns.length + (selectable ? 1 : 0)}
              className="h-24 text-center text-muted-foreground"
            >
              {emptyMessage}
            </DataTableCell>
          </DataTableRow>
        ) : (
          sortedData.map((row, rowIndex) => {
            const isSelected = selectedRows.includes(row);
            return (
              <DataTableRow
                key={getId(row, rowIndex)}
                selected={isSelected}
                onClick={() => onRowClick?.(row, rowIndex)}
                className={onRowClick ? 'cursor-pointer' : undefined}
              >
                {selectable && (
                  <DataTableCellCheckbox
                    checked={isSelected}
                    onCheckedChange={() => handleSelectRow(row)}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
                {columns.map((column) => (
                  <DataTableCell key={column.id} align={column.align}>
                    {column.cell({ row, rowIndex })}
                  </DataTableCell>
                ))}
              </DataTableRow>
            );
          })
        )}
      </DataTableBody>
    </DataTable>
  );
}
